<script>
import {
  required,
  minValue,
  integer,
  maxValue,
} from 'vuelidate/lib/validators';

import Tooltip from '@/components/widgets/errors/validations-tooltip.vue';

import scrollMixin from '@/mixins/scrollWheelMixin';

export default {
  props: {
    mode: { type: String, default: 'CONSUME' },
    quantityType: { type: String, default: 'unitary' },
    maxConsumption: { type: Number, default: null },
    currentInventory: { type: Number, default: 0 },
  },
  components: {
    Tooltip,
  },
  mixins: [scrollMixin],
  data() {
    return {
      quantity: 0,
      interval: null,
    };
  },
  methods: {
    wheelInput(event) {
      if (this.mode == 'CONSUME') {
        this.modifyQuantity(
          this.scrollMixinEvent(event, this.quantity, 0, this.maxConsumption)
        );
      } else {
        this.modifyQuantity(this.scrollMixinEvent(event, this.quantity, 0));
      }
    },
    minusDown(e) {
      if (e != null && e.cancelable) e.preventDefault();

      this.buttonUp();

      this.modifyQuantity(this.toFloat(this.quantity) - 1);

      this.timeout = setTimeout(() => {
        if (this.interval != null) clearInterval(this.interval);

        this.interval = setInterval(() => {
          this.modifyQuantity(this.toFloat(this.quantity) - 1);
        }, 40);
      }, 400);
    },
    plusDown(e) {
      if (e != null && e.cancelable) e.preventDefault();

      this.buttonUp();

      this.modifyQuantity(this.toFloat(this.quantity) + 1);

      this.timeout = setTimeout(() => {
        if (this.interval != null) clearInterval(this.interval);

        this.interval = setInterval(() => {
          this.modifyQuantity(this.toFloat(this.quantity) + 1);
        }, 40);
      }, 400);
    },
    buttonUp(e) {
      if (e != null && e.cancelable) e.preventDefault();

      if (this.timeout != null) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }

      if (this.interval != null) {
        clearInterval(this.interval);
        this.interval = null;
      }
    },
    modifyQuantity(newQuantity) {
      if (newQuantity < 0) {
        this.quantity = 0;
      } else if (
        this.mode == 'CONSUME' &&
        this.maxConsumption != null &&
        newQuantity > this.maxConsumption
      ) {
        this.quantity = this.maxConsumption;
      } else {
        this.quantity = newQuantity;
      }
    },
    confirmationClicked() {
      this.$v.quantity.$touch();
      if (!this.$v.quantity.$invalid) {
        this.$emit('confirmation-clicked', this.quantity);
      }
    },
  },
  validations() {
    let quantity = {
      required,
      minValue: minValue(0),
    };
    if (this.item != null && this.item.quantityType == 'unitary') {
      quantity.integer = integer;
    }

    if (this.mode == 'CONSUME') {
      quantity.maxValue = maxValue(this.maxConsumption);
    }

    return {
      quantity: quantity,
    };
  },
};
</script>

<template>
  <div class="main">
    <b-form-group class="quantity-input-group">
      <b-form-input
        class="custom-input"
        type="number"
        v-model="quantity"
        :step="quantityType == 'unitary' ? '1' : '.00001'"
        :min="0"
        :class="{ 'is-invalid': $v.quantity.$error }"
        @wheel.prevent="wheelInput($event)"
        @keydown.enter.prevent="confirmClicked"
        :placeholder="$t('items.quantity')"
      ></b-form-input>
      <Tooltip :validations="$v.quantity" />
    </b-form-group>

    <small
      class="text-center"
      :class="
        mode == 'CONSUME' && maxConsumption - quantity <= 0
          ? 'text-danger'
          : 'text-muted'
      "
    >
      <span v-if="mode == 'CONSUME'">
        {{ $t('items.availableQuantity') + `: ${maxConsumption - quantity}` }}
      </span>
      <span v-else>
        {{ $t('items.totalQuantity') + `: ${currentInventory + quantity}` }}
      </span>
    </small>

    <div class="add-remove-buttons">
      <div
        class="custom-plus-minus-button"
        :style="quantity <= 0 ? 'box-shadow: none' : ''"
        @mousedown="minusDown"
        @touchstart="minusDown"
        @mouseup="buttonUp"
        @touchend="buttonUp"
      >
        <i
          class="fas fa-minus"
          :class="quantity <= 0 ? 'text-secondary' : 'text-danger'"
        ></i>
      </div>
      <div
        class="custom-plus-minus-button"
        :style="
          mode == 'CONSUME' && quantity >= maxConsumption
            ? 'box-shadow: none'
            : ''
        "
        @mousedown="plusDown"
        @touchstart="plusDown"
        @mouseup="buttonUp"
        @touchend="buttonUp"
      >
        <i
          class="fas fa-plus"
          :class="
            mode == 'CONSUME' && quantity >= maxConsumption
              ? 'text-secondary'
              : 'text-success'
          "
        ></i>
      </div>
    </div>

    <b-button
      style="font-size: 1.8em"
      class="confirm-button"
      size="lg"
      :disabled="
        quantity <= 0 || (mode == 'CONSUME' && quantity > maxConsumption)
      "
      :style="
        quantity <= 0 || (mode == 'CONSUME' && quantity > maxConsumption)
          ? 'box-shadow: none'
          : ''
      "
      :variant="mode == 'CONSUME' ? 'danger' : 'success'"
      @click="confirmationClicked()"
    >
      {{ $t('items.' + mode.toLowerCase()) }}
    </b-button>
  </div>
</template>

<style scoped>
.main {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.quantity-input-group {
  margin-bottom: 0;
}

.custom-input {
  border: none !important;
  box-shadow: none !important;
  border-bottom: 1px solid rgb(233, 233, 233) !important;
  border-radius: 0 !important;
  width: min(300px, 80vw + 20px);
  text-align: center;
  font-size: 3.5em;
  padding: 20px 10px;
  overflow: hidden;
}

/* Remove arrows from number input */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

small {
  height: 22px;
  font-size: 1.2em;
  width: 100%;
}

.add-remove-buttons {
  display: flex;
  justify-content: center;
  margin: 20px 0;
}

.custom-plus-minus-button {
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  border: 2px solid rgb(214, 214, 214);
  border-radius: 15px;
  width: min(140px, 40vw);
  height: min(140px, 40vw);
  margin: 0 10px;
  font-size: 4em;
  box-shadow: 1px 1px 3px rgb(207, 207, 207);
  cursor: pointer;
}

.confirm-button {
  width: min(300px, 80vw + 20px);
  margin-top: 20px;
  box-shadow: 1px 1px 5px rgb(187, 187, 187);
}
</style>